import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal, ModalInterface } from '../Modal';
import Loader from 'react-loader-spinner';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import styles from './ReportForm.module.scss';
import globalStyles from '../../WifiConnect.module.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { slackService } from '../../../../api/slack/service';
import { AppConfig } from '../../../../constants/config';
import { Block } from '../Block/Block';
import {
  corporateDeviceTypeOptions,
  corporateRegionsOptions,
  timeout,
} from '../../../../constants/app';
import { getDeviceClient } from '../../../../api/devices/client';
import { DeviceResponse } from '../../../../api/devices/types';
import {
  CorporateEditForm,
  CorporateEditFormInterface,
} from './CorporateEditForm';
import { getRegion, getType } from '../../../../utils/generators';

export interface ConvertToCorpModalInterface {
  show(): void;
  hide(): void;
}

interface ConvertToCorpFormProps {
  device: DeviceResponse;
  onSuccess: Function;
  onCancel?: Function;
  onFinished: () => void;
  onOK: () => void;
}

export const ConvertToCorpModal = forwardRef<
  ConvertToCorpModalInterface,
  ConvertToCorpFormProps
>(({ device, onSuccess, onCancel, onFinished, onOK }, ref) => {
  const modalRef = useRef<ModalInterface>(null);

  const reportModalRef = useRef<CorporateEditFormInterface>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isReporting, setIsReporting] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    show() {
      if (modalRef.current) {
        modalRef.current.show();
        device.is_converting_to_wwiot_allowed
          ? modalRef.current.enableOK()
          : modalRef.current.disableOK();
      }
    },
    hide() {
      if (modalRef.current) {
        modalRef.current.hide();
      }
    },
  }));

  useEffect(() => cleanup(), []);

  const onSubmit = async () => {
    if (modalRef.current) {
      modalRef.current.toggleOK();
      modalRef.current.toggleCancel();
    }

    if (reportModalRef.current) {
      reportModalRef.current.show();
    }
  };

  const cleanup = () => {
    if (modalRef.current) {
      modalRef.current.disableOK();
    }

    setIsError(false);
  };

  return (
    <Modal
      ref={modalRef}
      title={
        <FormattedMessage
          id={
            device.is_converting_to_wwiot_allowed
              ? 'tabs.corporate.convertForm.form.title'
              : 'tabs.corporate.convertForm.form.warningTitle'
          }
          defaultMessage={
            device.is_converting_to_wwiot_allowed
              ? 'Current device owner is {owner}. Are you sure to move this device MAC address to be managed by corporate?'
              : 'Current device owner is {owner}. Managing by corp is not allowed.'
          }
          values={{ owner: device.owner }}
        />
      }
      onOK={onSubmit}
      onCancel={() => {
        if (onCancel) {
          onCancel();
        }

        cleanup();
      }}
      okText={
        isReporting ? (
          <Loader type="Oval" color="#FFF" height={13} width={13} />
        ) : (
          <FormattedMessage
            id="tabs.corporate.convertForm.form.action"
            defaultMessage="Confirm"
          />
        )
      }
      isSelfClosable={false}
    >
      <CorporateEditForm
        ref={reportModalRef}
        currentMAC={device.mac}
        deviceData={{
          mac: device.mac,
          type: corporateDeviceTypeOptions.includes(getType(device.name))
            ? getType(device.name)
            : '',
          otherType: '',
          location: device.location_uuid,
          region: corporateRegionsOptions.includes(getRegion(device.name))
            ? getRegion(device.name)
            : '',
          notes: device.notes,
        }}
        title={
          <FormattedMessage
            id="modal.headings.editFormTitle"
            defaultMessage="Edit device"
          />
        }
        onOK={onOK}
        onCancel={() => {
          onOK();
          if (modalRef.current) {
            modalRef.current.hide();
          }
        }}
        onFinished={() => {
          onFinished();
          if (modalRef.current) {
            modalRef.current.hide();
          }
        }}
        onMessage={(msg: JSX.Element) => {}}
        isSwitchToCorpForm={true}
      />
      {isError ? (
        <Block>
          <div
            className={classNames(
              globalStyles.notification,
              globalStyles.errorMsgInReportModal,
            )}
          >
            <FormattedMessage
              id={`tabs.corporate.convertForm.error`}
              defaultMessage="Internal error. Check your Internet connection and try again"
            />
          </div>
        </Block>
      ) : (
        <></>
      )}
    </Modal>
  );
});
